<template>
  <v-card class="py-3 cardScroll" density="compact" flat min-height="500" max-height="88vh" max-width="543">

    <!-- ── Search bar ─────────────────────────────────── -->
    <div class="ws-search-wrap">
      <div class="ws-search-box">
        <v-icon size="16" color="#9ca3af" class="ws-search-icon">mdi-magnify</v-icon>
        <input
          v-model="searchQuery"
          class="ws-search-input"
          placeholder="Search workspaces or accounts…"
          type="text"
          autocomplete="off"
        />
        <button v-if="searchQuery" class="ws-clear-btn" @click="searchQuery = ''">
          <v-icon size="14" color="#9ca3af">mdi-close</v-icon>
        </button>
      </div>
    </div>

    <!-- ── Workspace list ─────────────────────────────── -->
    <template v-if="filteredWorkSpaceData.length">
      <div v-for="item in filteredWorkSpaceData" :key="item.work_spaces.id">
        <div v-if="item && item.work_spaces.name && item.account_data && item.account_data.length > 0">
          <v-text class="ml-3 px-11" :class="{ 'px-11': item.account_data.length > 3 }">
            <b>{{ item.work_spaces.name }}</b>
          </v-text>

          <div v-if="item.account_data.length > 3">
            <Carousel :value="item.account_data" :numVisible="3" :numScroll="1" :responsiveOptions="responsiveOptions">
              <template #item="slotProps">
                <v-card elevation="0" density="compact" class="mainCard mx-3 my-3 px-3"
                  @click="getLink(slotProps.data, item.work_spaces, item.account_data.length)" :class="{ active: slotProps.data.showActive }">
                  <v-card-title class="workSpace pb-1">{{ slotProps.data.name }}
                    <v-tooltip activator="parent" location="top">{{ slotProps.data.name }}</v-tooltip>
                  </v-card-title>
                  <v-card-text class="chipTxt">
                    Open issues
                    <div class="chipD">
                      <v-chip :class="{ active1: slotProps.data.showActive }" class="chip mt-2" label>
                        <span class="chipTxtNO">{{ slotProps.data.no_of_issues }}</span>
                      </v-chip>
                    </div>
                  </v-card-text>
                </v-card>
              </template>
            </Carousel>
          </div>

          <div v-else class="else-card-container px-11" :class="{ 'px-11': item.account_data.length > 3 }">
            <div v-for="data in item.account_data" :key="data.id">
              <v-card elevation="0" density="compact" class="mainCard mx-3 my-3 px-3"
                @click="getLink(data, item.work_spaces, item.account_data.length)" :class="{ active: data.showActive }">
                <v-card-title class="workSpace pb-1">{{ data.name }}
                  <v-tooltip activator="parent" location="top">{{ data.name }}</v-tooltip>
                </v-card-title>
                <v-card-text class="chipTxt">
                  Open issues
                  <div class="chipD">
                    <v-chip :class="{ active1: data.showActive }" class="chip mt-2" label>
                      <span class="chipTxtNO">{{ data.no_of_issues }}</span>
                    </v-chip>
                  </div>
                </v-card-text>
              </v-card>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- ── Empty state ───────────────────────────────── -->
    <div v-else class="ws-empty">
      <v-icon size="32" color="#d1d5db">mdi-folder-search-outline</v-icon>
      <span>No workspaces match <strong>"{{ searchQuery }}"</strong></span>
    </div>

  </v-card>
</template>


<script>
import Carousel from 'primevue/carousel';
import WorkSpaceService from "@/api/service/WorkSpace/WorkSpaceService";

export default {
  components: { Carousel },

  data() {
    return {
      workSpaceData: [],
      searchQuery: '',
      responsiveOptions: [
        { breakpoint: '1199px', numVisible: 3, numScroll: 3 },
        { breakpoint: '991px',  numVisible: 2, numScroll: 2 },
        { breakpoint: '767px',  numVisible: 1, numScroll: 1 },
      ],
    };
  },

  computed: {
    filteredWorkSpaceData() {
      const q = this.searchQuery.trim().toLowerCase();
      if (!q) return this.workSpaceData;

      const result = [];

      for (const item of this.workSpaceData) {
        if (!item || !item.work_spaces || !item.work_spaces.name) continue;

        const ws = item.work_spaces;
        // Exact startsWith match on workspace name or code
        const wsMatch =
          (ws.name && ws.name.toLowerCase().startsWith(q)) ||
          (ws.code && ws.code.toLowerCase().startsWith(q));

        if (wsMatch) {
          // workspace matches — show full group
          result.push(item);
        } else {
          // filter accounts by exact startsWith on name or code
          const matchedAccounts = (item.account_data || []).filter(acc =>
            (acc.name && acc.name.toLowerCase().startsWith(q)) ||
            (acc.code && acc.code.toLowerCase().startsWith(q))
          );
          if (matchedAccounts.length > 0) {
            result.push({ ...item, account_data: matchedAccounts });
          }
        }
      }

      return result;
    },
  },

  created() {
    this.getWorkSpace();
  },

  methods: {
    getLink($account, item, accountCount) {
      let status = this.$store.getters.getMyspace;

      // Multiple accounts → land on "All Accounts" consolidated view directly.
      // Single account → land on that account's issues directly.
      const accountEntry = accountCount > 1
        ? { id: 0, name: "All Accounts" }
        : { id: $account.id, name: $account.name };

      let $wsDefaults = {
        workspace: { id: item.id, name: item.name },
        project: { id: 0, name: "All Projects" },
        account: accountEntry,
      };
      localStorage.setItem("_WS_DEFAULTS_", JSON.stringify($wsDefaults));
      localStorage.setItem("workSpaceId", item.id);
      this.$store.commit("SET_REFRESH_BREADCRUMP", true);
      this.$store.commit("CHANGE_MY_SPACE", !status);
      this.$router.push(`/issueConsolidated`);
      this.$emit("closeMenu");
    },

    async getWorkSpace() {
      let res = await WorkSpaceService.getMySpace();
      this.workSpaceData = res;
    },
  },
};
</script>

<style scoped>
/* ── Search bar ─────────────────────────────────────── */
.ws-search-wrap {
  padding: 4px 16px 12px;
  position: sticky;
  top: 0;
  z-index: 2;
  background: #fff;
}

.ws-search-box {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 7px 12px;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.ws-search-box:focus-within {
  border-color: #a5b4fc;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
  background: #fff;
}

.ws-search-icon { flex-shrink: 0; }

.ws-search-input {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 13px;
  color: #111827;
  font-family: inherit;
}
.ws-search-input::placeholder { color: #9ca3af; }

.ws-clear-btn {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  line-height: 1;
  border-radius: 4px;
  transition: opacity 0.15s;
}
.ws-clear-btn:hover { opacity: 0.6; }

/* ── Empty state ────────────────────────────────────── */
.ws-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 48px 24px;
  color: #9ca3af;
  font-size: 13px;
  text-align: center;
}
.ws-empty strong { color: #6b7280; }

/* ── Existing styles (unchanged) ────────────────────── */
>>>.p-carousel .p-carousel-indicators {
  padding: 1rem;
  display: none;
}

.column { max-width: fit-content; }

.chipTxtNO {
  font-size: 12px;
  font-weight: 700;
}

.mainCard {
  width: 140px;
  height: 110px;
  border-radius: 4px !important;
  background-color: rgb(241, 241, 241);
}

.active {
  background-color: #c7f0d0;
  color: black;
  width: 140px;
  height: 110px;
  border-radius: 4px !important;
}

.chipTxt {
  font-size: 12px;
  font-weight: 500;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
}

.workSpace {
  font-weight: 700;
  font-size: 14px;
  color: #636363;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

>>>.v-chip--variant-tonal .v-chip__underlay {
  background-color: inherit;
  color: #272727 !important;
}

>>>.v-chip__content { color: #272727 !important; }

.chip { background: #dfdbf6; }

.chipD {
  display: flex;
  justify-content: center;
}

.chip.active1 { background-color: #f0e7f8 !important; }

.cardScroll::-webkit-scrollbar { width: 6px; }
.cardScroll::-webkit-scrollbar-track { background: #e8f3f9; }
.cardScroll::-webkit-scrollbar-thumb { background: rgb(0, 0, 0, 0.12); }

.else-card-container { display: flex; }
</style>
